import type {
  PrintLayoutConfig,
  StorePrinter,
} from "@medicorepay/shared/types";
import {
  GOLDEN_INVOICE_PRINT_DOCUMENT,
  defaultInvoiceLayout,
  renderInvoiceHtmlArtifact,
} from "@medicorepay/shared";
import { ChevronLeft, Eye, RotateCcw, Save } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../lib/toast";
import { printerService } from "../../services/printer.service";
import { useAuthStore } from "../../stores/authStore";

const DEFAULT_FORMAT = "DOT_MATRIX_9_5X5_5" as const;
const DEFAULT_TEMPLATE_NAME = "9.5 x 5.5 Fanfold";
const DEFAULT_LAYOUT: PrintLayoutConfig = defaultInvoiceLayout(DEFAULT_FORMAT);

// ─── Quick Templates ─────────────────────────────────────────────────────
const TEMPLATES: {
  name: string;
  desc: string;
  format: Parameters<typeof defaultInvoiceLayout>[0];
  layout: Partial<PrintLayoutConfig>;
}[] = [
  {
    name: "Standard A4",
    desc: "Full details with all columns — ideal for A4 laser printers",
    format: "A4",
    layout: {
      ...defaultInvoiceLayout("A4"),
      fontSize: 10,
      fontFamily: "sans-serif",
      lineSpacing: 1.3,
      compactMode: false,
      showHeader: true,
      showFooter: true,
      showGST: true,
      showDoctor: true,
      showHSN: true,
      showBatch: true,
      showMfr: false,
      showSKU: false,
      showSchedule: false,
      showKitIdentity: false,
      showKitComponents: false,
      showQuantity: true,
      showMRP: true,
      showExpiry: true,
      showDiscount: true,
      showTaxBreakdown: true,
      showPatientInfo: true,
    },
  },
  {
    name: "9.5 x 5.5 Fanfold",
    desc: "Wide half-sheet pharmacy invoice for continuous dot-matrix paper",
    format: "DOT_MATRIX_9_5X5_5",
    layout: {
      ...defaultInvoiceLayout("DOT_MATRIX_9_5X5_5"),
      fontSize: 8,
      fontFamily: "monospace",
      lineSpacing: 1.15,
      margins: { top: 5, right: 12.7, bottom: 5, left: 12.7 },
      compactMode: true,
      showHeader: true,
      showFooter: true,
      showGST: true,
      showDoctor: true,
      showHSN: true,
      showBatch: true,
      showMfr: false,
      showSKU: false,
      showSchedule: false,
      showKitIdentity: false,
      showKitComponents: false,
      showQuantity: true,
      showMRP: true,
      showExpiry: true,
      showDiscount: true,
      showTaxBreakdown: true,
      showPatientInfo: true,
      maxItemColumns: 10,
      headerLines: [
        "{{storeName}}",
        "{{storeAddress}}, {{city}} - {{pincode}}",
      ],
      footerLines: [
        "E. & O.E.",
        "Goods once sold will not be taken back or exchanged.",
        "Subject to {{city}} jurisdiction.",
      ],
    },
  },
  {
    name: "Compact Thermal",
    desc: "Minimal columns for 80mm thermal receipt printers",
    format: "80MM",
    layout: {
      ...defaultInvoiceLayout("80MM"),
      fontSize: 8,
      fontFamily: "monospace",
      lineSpacing: 1.1,
      compactMode: true,
      showHeader: true,
      showFooter: true,
      showGST: true,
      showDoctor: false,
      showHSN: false,
      showBatch: true,
      showMfr: false,
      showSKU: false,
      showSchedule: false,
      showKitIdentity: false,
      showKitComponents: false,
      showQuantity: true,
      showMRP: true,
      showExpiry: true,
      showDiscount: true,
      showTaxBreakdown: false,
      showPatientInfo: true,
      margins: { top: 2, right: 2, bottom: 2, left: 2 },
    },
  },
  {
    name: "Pharmacy Standard",
    desc: "Common Indian pharmacy format with batch, MRP, expiry, discount",
    format: "A4",
    layout: {
      ...defaultInvoiceLayout("A4"),
      fontSize: 9,
      fontFamily: "monospace",
      lineSpacing: 1.2,
      compactMode: false,
      showHeader: true,
      showFooter: true,
      showGST: true,
      showDoctor: true,
      showHSN: true,
      showBatch: true,
      showMfr: false,
      showSKU: false,
      showSchedule: false,
      showKitIdentity: false,
      showKitComponents: false,
      showQuantity: true,
      showMRP: true,
      showExpiry: true,
      showDiscount: true,
      showTaxBreakdown: true,
      showPatientInfo: true,
    },
  },
  {
    name: "Minimal",
    desc: "Clean, minimal invoice with essential info only",
    format: "A4",
    layout: {
      ...defaultInvoiceLayout("A4"),
      fontSize: 10,
      fontFamily: "sans-serif",
      lineSpacing: 1.3,
      compactMode: false,
      showHeader: true,
      showFooter: false,
      showGST: true,
      showDoctor: false,
      showHSN: false,
      showBatch: false,
      showMfr: false,
      showSKU: false,
      showSchedule: false,
      showKitIdentity: false,
      showKitComponents: false,
      showQuantity: true,
      showMRP: true,
      showExpiry: false,
      showDiscount: true,
      showTaxBreakdown: false,
      showPatientInfo: true,
    },
  },
];

// ─── Column definition for the visual builder ────────────────────────────
interface ColumnDef {
  key: keyof PrintLayoutConfig;
  label: string;
  shortLabel: string;
  description: string;
  group: "header" | "items" | "footer";
}

const COLUMN_DEFS: ColumnDef[] = [
  // Header/Sections
  {
    key: "showHeader",
    label: "Store Header",
    shortLabel: "Header",
    description: "Master switch for all pharmacy header blocks",
    group: "header",
  },
  {
    key: "showStoreIdentity",
    label: "Shop Name & Address",
    shortLabel: "Shop",
    description: "Pharmacy name and postal address",
    group: "header",
  },
  {
    key: "showStoreContact",
    label: "Contact Details",
    shortLabel: "Contact",
    description: "Phone, WhatsApp number and email",
    group: "header",
  },
  {
    key: "showStoreLicenses",
    label: "License Details",
    shortLabel: "Licenses",
    description: "GSTIN, drug licence and FSSAI number",
    group: "header",
  },
  {
    key: "showGST",
    label: "GST Details",
    shortLabel: "GST",
    description: "GSTIN in header and GST values in invoice totals",
    group: "header",
  },
  {
    key: "showPatientInfo",
    label: "Customer Info",
    shortLabel: "Customer",
    description: "Customer/Patient name and phone",
    group: "header",
  },
  {
    key: "showDoctor",
    label: "Doctor Info",
    shortLabel: "Doctor",
    description: "Prescribing doctor name and registration",
    group: "header",
  },
  // Item columns
  {
    key: "showHSN",
    label: "HSN Code",
    shortLabel: "HSN",
    description: "Harmonized System Nomenclature code",
    group: "items",
  },
  {
    key: "showBatch",
    label: "Batch Number",
    shortLabel: "Batch",
    description: "Manufacturing batch identifier",
    group: "items",
  },
  {
    key: "showExpiry",
    label: "Expiry Date",
    shortLabel: "Expiry",
    description: "Product expiration date",
    group: "items",
  },
  {
    key: "showMfr",
    label: "Manufacturer",
    shortLabel: "Mfr",
    description: "Product manufacturer name",
    group: "items",
  },
  {
    key: "showSKU",
    label: "SKU",
    shortLabel: "SKU",
    description: "Product stock-keeping identifier",
    group: "items",
  },
  {
    key: "showSchedule",
    label: "Schedule Type",
    shortLabel: "Schedule",
    description: "Drug schedule (H, H1, X)",
    group: "items",
  },
  {
    key: "showKitIdentity",
    label: "Kit Identity",
    shortLabel: "Kit ID",
    description: "Commercial kit code and pack identity",
    group: "items",
  },
  {
    key: "showKitComponents",
    label: "Kit Components",
    shortLabel: "Components",
    description: "Component names and allocated batch details",
    group: "items",
  },
  {
    key: "showChargeDetails",
    label: "Charge Details",
    shortLabel: "Charge code",
    description: "Internal charge type and code below charge names",
    group: "items",
  },
  {
    key: "showQuantity",
    label: "Quantity Column",
    shortLabel: "Qty",
    description: "Paid, promotional, replacement and sample quantities",
    group: "items",
  },
  {
    key: "showMRP",
    label: "MRP Column",
    shortLabel: "MRP",
    description: "Maximum Retail Price per unit",
    group: "items",
  },
  {
    key: "showDiscount",
    label: "Discount Column",
    shortLabel: "Discount",
    description: "Per-item discount amount (batch-level + scheme)",
    group: "items",
  },
  {
    key: "showTaxBreakdown",
    label: "Tax Breakdown",
    shortLabel: "CGST/SGST",
    description: "CGST% and SGST% columns per item",
    group: "items",
  },
  // Footer
  {
    key: "showTenders",
    label: "Tenders",
    shortLabel: "Tenders",
    description: "Cash, UPI and other payment method breakdown",
    group: "footer",
  },
  {
    key: "showFooter",
    label: "Footer",
    shortLabel: "Footer",
    description: "Thank you message, custom text",
    group: "footer",
  },
];

// ─── Component ───────────────────────────────────────────────────────────
export function InvoiceCustomizationPage() {
  const navigate = useNavigate();
  const selectedStoreId = useAuthStore((s) => s.selectedStoreId);

  const [printer, setPrinter] = useState<StorePrinter | null>(null);
  const [layout, setLayout] = useState<PrintLayoutConfig>({
    ...DEFAULT_LAYOUT,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState(
    DEFAULT_TEMPLATE_NAME,
  );
  const [previewFormat, setPreviewFormat] =
    useState<Parameters<typeof defaultInvoiceLayout>[0]>(DEFAULT_FORMAT);

  const loadConfig = useCallback(async () => {
    if (!selectedStoreId) return;
    try {
      setLoading(true);
      const config = await printerService.getDefaultForStore(
        selectedStoreId,
        "BILLING",
      );
      setPrinter(config);
      if (config?.layoutConfig) {
        setLayout({ ...DEFAULT_LAYOUT, ...(config.layoutConfig as any) });
      }
    } catch {
      // No default printer — use defaults
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateLayout = (key: keyof PrintLayoutConfig, value: any) => {
    setLayout((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const applyTemplate = (template: (typeof TEMPLATES)[0]) => {
    setLayout((prev) => ({ ...prev, ...template.layout }));
    setSelectedTemplateName(template.name);
    setPreviewFormat(template.format);
    setHasChanges(true);
    showToast.success(`"${template.name}" template applied`);
  };

  const handleSave = async () => {
    if (!printer) {
      showToast.error(
        "No default billing printer configured. Please add one in Printer Settings first.",
      );
      return;
    }
    try {
      setSaving(true);
      await printerService.update(printer.id, { layoutConfig: layout });
      setHasChanges(false);
      showToast.success("Invoice layout saved!");
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLayout(defaultInvoiceLayout(DEFAULT_FORMAT));
    setSelectedTemplateName(DEFAULT_TEMPLATE_NAME);
    setPreviewFormat(DEFAULT_FORMAT);
    setHasChanges(true);
  };

  // Sample invoice data for live preview
  const sampleItems = [
    {
      name: "Paracetamol 500mg",
      sku: "PCM-500",
      hsn: "3004",
      batch: "B2601",
      expiry: "Dec 26",
      sch: "H",
      mfr: "Cipla",
      qty: 10,
      free: 0,
      mrp: 25.0,
      rate: 20.0,
      discount: 50.0,
      cgst: 2.5,
      sgst: 2.5,
      taxable: 200.0,
      amount: 210.0,
    },
    {
      name: "Amoxicillin 250mg",
      sku: "AMX-250",
      hsn: "3004",
      batch: "B2602",
      expiry: "Mar 27",
      sch: "-",
      mfr: "GSK",
      qty: 6,
      free: 2,
      mrp: 45.0,
      rate: 35.0,
      discount: 60.0,
      cgst: 2.5,
      sgst: 2.5,
      taxable: 210.0,
      amount: 220.5,
    },
    {
      name: "Crocin Advance",
      sku: "CRC-ADV",
      hsn: "3004",
      batch: "B2603",
      expiry: "Jun 26",
      sch: "-",
      mfr: "GSK",
      qty: 3,
      free: 0,
      mrp: 85.0,
      rate: 72.0,
      discount: 39.0,
      cgst: 6,
      sgst: 6,
      taxable: 216.0,
      amount: 228.0,
    },
  ];

  const totalTaxable = sampleItems.reduce((s, i) => s + i.taxable, 0);
  const totalCgst = sampleItems.reduce((s, i) => s + i.cgst, 0);
  const totalSgst = sampleItems.reduce((s, i) => s + i.sgst, 0);
  const totalDiscount = sampleItems.reduce((s, i) => s + i.discount, 0);
  const totalAmount = sampleItems.reduce((s, i) => s + i.amount, 0);

  const previewHtml = useMemo(
    () =>
      renderInvoiceHtmlArtifact(
        GOLDEN_INVOICE_PRINT_DOCUMENT,
        previewFormat,
        layout,
      ),
    [layout, previewFormat],
  );

  if (!selectedStoreId) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Please select a store first.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-500">Loading invoice settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/settings")}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Invoice Customization
              </h1>
              <p className="text-xs text-gray-500">
                Choose what appears on your printed invoices
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ─── Left Panel: Controls ─────────────────────────── */}
          <div className="lg:col-span-2 space-y-5">
            {/* Quick Templates */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Quick Templates
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.name}
                    onClick={() => applyTemplate(t)}
                    className={`text-left p-3 rounded-lg border transition group ${
                      selectedTemplateName === t.name
                        ? "border-cyan-500 ring-1 ring-cyan-500 bg-cyan-50/40"
                        : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700">
                      {t.name}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">
                      {t.desc}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Header / Sections */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Invoice Sections
              </h3>
              <div className="space-y-2">
                {COLUMN_DEFS.filter((c) => c.group === "header").map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${
                      (layout as any)[col.key]
                        ? "border-blue-200 bg-blue-50/40"
                        : "border-gray-100 bg-gray-50/50"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {col.label}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {col.description}
                      </p>
                    </div>
                    <div
                      className={`relative w-10 h-6 rounded-full transition ${(layout as any)[col.key] ? "bg-blue-600" : "bg-gray-300"}`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${(layout as any)[col.key] ? "translate-x-5" : "translate-x-1"}`}
                      />
                      <input
                        type="checkbox"
                        checked={(layout as any)[col.key]}
                        onChange={(e) =>
                          updateLayout(col.key, e.target.checked)
                        }
                        className="sr-only"
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Item details and columns */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Item Details & Columns
              </h3>
              <p className="text-[10px] text-gray-400 mb-3">
                Hide optional row details to make each printed item more compact
              </p>
              <div className="space-y-2">
                {COLUMN_DEFS.filter((c) => c.group === "items").map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${
                      (layout as any)[col.key]
                        ? "border-blue-200 bg-blue-50/40"
                        : "border-gray-100 bg-gray-50/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${(layout as any)[col.key] ? "bg-blue-500" : "bg-gray-300"}`}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {col.label}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {col.description}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`relative w-10 h-6 rounded-full transition ${(layout as any)[col.key] ? "bg-blue-600" : "bg-gray-300"}`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${(layout as any)[col.key] ? "translate-x-5" : "translate-x-1"}`}
                      />
                      <input
                        type="checkbox"
                        checked={(layout as any)[col.key]}
                        onChange={(e) =>
                          updateLayout(col.key, e.target.checked)
                        }
                        className="sr-only"
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Footer / Misc */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Footer & Styling
              </h3>
              <div className="space-y-3">
                {COLUMN_DEFS.filter((c) => c.group === "footer").map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${
                      (layout as any)[col.key]
                        ? "border-blue-200 bg-blue-50/40"
                        : "border-gray-100 bg-gray-50/50"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {col.label}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {col.description}
                      </p>
                    </div>
                    <div
                      className={`relative w-10 h-6 rounded-full transition ${(layout as any)[col.key] ? "bg-blue-600" : "bg-gray-300"}`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${(layout as any)[col.key] ? "translate-x-5" : "translate-x-1"}`}
                      />
                      <input
                        type="checkbox"
                        checked={(layout as any)[col.key]}
                        onChange={(e) =>
                          updateLayout(col.key, e.target.checked)
                        }
                        className="sr-only"
                      />
                    </div>
                  </label>
                ))}

                {/* Compact Mode Toggle */}
                <label
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${
                    layout.compactMode
                      ? "border-blue-200 bg-blue-50/40"
                      : "border-gray-100 bg-gray-50/50"
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      Compact Mode
                    </p>
                    <p className="text-[10px] text-gray-400">
                      Reduce padding & font size for thermal printers
                    </p>
                  </div>
                  <div
                    className={`relative w-10 h-6 rounded-full transition ${layout.compactMode ? "bg-blue-600" : "bg-gray-300"}`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${layout.compactMode ? "translate-x-5" : "translate-x-1"}`}
                    />
                    <input
                      type="checkbox"
                      checked={layout.compactMode}
                      onChange={(e) =>
                        updateLayout("compactMode", e.target.checked)
                      }
                      className="sr-only"
                    />
                  </div>
                </label>

                {/* Font Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Font Size: {layout.fontSize}pt
                  </label>
                  <input
                    type="range"
                    min={6}
                    max={14}
                    value={layout.fontSize}
                    onChange={(e) =>
                      updateLayout("fontSize", Number(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>Small</span>
                    <span>Large</span>
                  </div>
                </div>

                {/* Custom Footer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Footer Message
                  </label>
                  <textarea
                    value={(layout.footerLines || []).join("\n")}
                    onChange={(e) =>
                      updateLayout(
                        "footerLines",
                        e.target.value.split("\n").filter(Boolean),
                      )
                    }
                    rows={2}
                    placeholder="Thank you! Visit again."
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ─── Right Panel: Live Preview ────────────────────── */}
          <div className="lg:col-span-3">
            <div className="sticky top-20">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700">
                  Live Preview
                </h3>
                <span className="text-[10px] text-gray-400 ml-auto">
                  Changes reflect instantly
                </span>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <iframe
                  title="Canonical invoice preview"
                  srcDoc={previewHtml}
                  sandbox="allow-same-origin"
                  className="block h-[72vh] min-h-[640px] w-full bg-white"
                />
                {false && (
                  <div
                    className="hidden"
                    style={{
                      fontFamily: layout.fontFamily || "monospace",
                      fontSize: `${layout.fontSize}pt`,
                      lineHeight: layout.lineSpacing,
                    }}
                  >
                    {/* Preview Header */}
                    {layout.showHeader && (
                      <div className="text-center mb-4 pb-3 border-b border-dashed border-gray-300">
                        <h2
                          className="font-bold text-gray-900"
                          style={{ fontSize: `${layout.fontSize + 4}pt` }}
                        >
                          Your Pharmacy Store
                        </h2>
                        <p className="text-gray-500 text-xs mt-1">
                          123 Medical Complex, Health City - 560001
                        </p>
                        <p className="text-gray-500 text-xs">
                          Ph: +91 98765 43210
                        </p>
                        {layout.showGST && (
                          <div className="text-xs text-gray-400 mt-1 font-mono">
                            GSTIN: 29ABCDE1234F1ZK | DL: KA-BNG-123456
                          </div>
                        )}
                      </div>
                    )}

                    {/* Invoice Info */}
                    <div className="flex justify-between items-start mb-3 text-xs">
                      <div>
                        <span className="font-semibold text-gray-900">
                          INV-20260223-0001
                        </span>
                        <br />
                        <span className="text-gray-500">
                          23 Feb 2026, 10:30 AM
                        </span>
                      </div>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">
                        PAID
                      </span>
                    </div>

                    {/* Customer / Doctor */}
                    <div className="flex gap-4 mb-4 text-xs">
                      {layout.showPatientInfo && (
                        <div className="flex-1 bg-gray-50 rounded p-2">
                          <p className="text-gray-400 text-[10px] font-bold uppercase mb-1">
                            Bill To
                          </p>
                          <p className="font-semibold text-gray-900">
                            Rajesh Kumar
                          </p>
                          <p className="text-gray-500">+91 98765 43210</p>
                        </div>
                      )}
                      {layout.showDoctor && (
                        <div className="flex-1 bg-gray-50 rounded p-2">
                          <p className="text-gray-400 text-[10px] font-bold uppercase mb-1">
                            Doctor
                          </p>
                          <p className="font-semibold text-gray-900">
                            Dr. Anand Sharma
                          </p>
                          <p className="text-gray-500 font-mono">
                            Reg: KMC-12345
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Items Table */}
                    <div className="mb-4 overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left py-1.5 text-[9px] font-bold text-gray-400">
                              #
                            </th>
                            <th className="text-left py-1.5 text-[9px] font-bold text-gray-400">
                              Item
                            </th>
                            {layout.showHSN && (
                              <th className="text-left py-1.5 text-[9px] font-bold text-gray-400">
                                HSN
                              </th>
                            )}
                            {layout.showBatch && (
                              <th className="text-center py-1.5 text-[9px] font-bold text-gray-400">
                                Batch
                              </th>
                            )}
                            {layout.showExpiry && (
                              <th className="text-center py-1.5 text-[9px] font-bold text-gray-400">
                                Exp
                              </th>
                            )}
                            {layout.showMfr && (
                              <th className="text-left py-1.5 text-[9px] font-bold text-gray-400">
                                Mfr
                              </th>
                            )}
                            {layout.showSchedule && (
                              <th className="text-center py-1.5 text-[9px] font-bold text-gray-400">
                                Sch
                              </th>
                            )}
                            {layout.showQuantity && (
                              <>
                                <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                                  Qty
                                </th>
                                <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                                  Free
                                </th>
                              </>
                            )}
                            {layout.showMRP && (
                              <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                                MRP
                              </th>
                            )}
                            <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                              Rate
                            </th>
                            {layout.showDiscount && (
                              <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                                Disc
                              </th>
                            )}
                            {layout.showTaxBreakdown && (
                              <>
                                <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                                  CGST%
                                </th>
                                <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                                  SGST%
                                </th>
                              </>
                            )}
                            <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                              Taxable
                            </th>
                            <th className="text-right py-1.5 text-[9px] font-bold text-gray-400">
                              Amt
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sampleItems.map((item, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="py-1 text-gray-400">{i + 1}</td>
                              <td className="py-1">
                                <span className="font-medium text-gray-900">
                                  {item.name}
                                </span>
                              </td>
                              {layout.showHSN && (
                                <td className="py-1 text-gray-500 font-mono">
                                  {item.hsn}
                                </td>
                              )}
                              {layout.showBatch && (
                                <td className="py-1 text-center text-gray-500 font-mono">
                                  {item.batch}
                                </td>
                              )}
                              {layout.showExpiry && (
                                <td className="py-1 text-center text-gray-500">
                                  {item.expiry}
                                </td>
                              )}
                              {layout.showMfr && (
                                <td className="py-1 text-gray-500">
                                  {item.mfr}
                                </td>
                              )}
                              {layout.showSchedule && (
                                <td className="py-1 text-center">
                                  {item.sch !== "-" ? (
                                    <span className="text-[9px] font-bold text-orange-700 bg-orange-50 px-1 rounded">
                                      {item.sch}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              )}
                              {layout.showQuantity && (
                                <>
                                  <td className="py-1 text-right font-medium">
                                    {item.qty}
                                  </td>
                                  <td className="py-1 text-right text-emerald-600">
                                    {item.free}
                                  </td>
                                </>
                              )}
                              {layout.showMRP && (
                                <td className="py-1 text-right text-gray-500">
                                  ₹{item.mrp.toFixed(2)}
                                </td>
                              )}
                              <td className="py-1 text-right text-gray-600">
                                ₹{item.rate.toFixed(2)}
                              </td>
                              {layout.showDiscount && (
                                <td className="py-1 text-right text-emerald-600 font-medium">
                                  {item.discount > 0
                                    ? `₹${item.discount.toFixed(2)}`
                                    : "-"}
                                </td>
                              )}
                              {layout.showTaxBreakdown && (
                                <>
                                  <td className="py-1 text-right text-gray-400">
                                    2.5%
                                  </td>
                                  <td className="py-1 text-right text-gray-400">
                                    2.5%
                                  </td>
                                </>
                              )}
                              <td className="py-1 text-right text-gray-600">
                                ₹{item.taxable.toFixed(2)}
                              </td>
                              <td className="py-1 text-right font-bold text-gray-900">
                                ₹{item.amount.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div className="border-t border-dashed border-gray-300 pt-3">
                      <div className="w-2/3 ml-auto space-y-1 text-xs">
                        <div className="flex justify-between text-gray-600">
                          <span>Taxable Amount</span>
                          <span className="font-medium">
                            ₹{totalTaxable.toFixed(2)}
                          </span>
                        </div>
                        {layout.showTaxBreakdown && (
                          <>
                            <div className="flex justify-between text-gray-500">
                              <span>CGST</span>
                              <span>₹{totalCgst.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-gray-500">
                              <span>SGST</span>
                              <span>₹{totalSgst.toFixed(2)}</span>
                            </div>
                          </>
                        )}
                        {layout.showDiscount && totalDiscount > 0 && (
                          <div className="flex justify-between text-emerald-600">
                            <span>Item Discounts</span>
                            <span className="font-medium">
                              -₹{totalDiscount.toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1 mt-1">
                          <span>Net Total</span>
                          <span className="text-blue-600 text-sm">
                            ₹{totalAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    {layout.showFooter && (
                      <div className="text-center text-gray-400 text-[10px] border-t border-dashed border-gray-300 pt-2 mt-3">
                        {(
                          layout.footerLines || ["Thank you! Visit again."]
                        ).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoiceCustomizationPage;
